import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import PageHeader from '@/components/layout/page-header'
import { TrailerList } from '@/features/vehicles/components/trailer/trailer-list'
import { TruckList } from '@/features/vehicles/components/truck/truck-list'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute('/dashboard/vehicles/')({
  head: () => ({ meta: [{ title: localizedPageTitle('vehicles') }] }),
  component: VehiclesPage,
})

type VehicleType = 'trucks' | 'trailers'

export default function VehiclesPage() {
  useDocumentTitle('vehicles')
  const { t } = useTranslation('vehicles')

  const [vehicleType, setVehicleType] = useState<VehicleType>('trucks')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: 300 })

  const handleVehicleTypeChange = (value: VehicleType) => {
    setVehicleType(value)
    setSearchQuery('')
  }

  return (
    <div className="flex w-full">
      <div className="min-w-0 flex-1 space-y-4">
        <PageHeader
          title={t('title')}
          subtitle={t('subtitle')}
          hideBackButton
        />

        <Card className="bg-background w-full max-md:rounded-none max-md:border-0 max-md:bg-transparent max-md:shadow-none">
          <CardHeader className="max-md:px-0">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <CardTitle>{t('allVehicles')}</CardTitle>
                <CardDescription>{t('subtitle')}</CardDescription>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center md:gap-4">
                <Select
                  value={vehicleType}
                  onValueChange={(value) =>
                    handleVehicleTypeChange(value as VehicleType)
                  }
                >
                  <SelectTrigger className="h-11 w-full sm:w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trucks">{t('tabs.trucks')}</SelectItem>
                    <SelectItem value="trailers">
                      {t('tabs.trailers')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative w-full md:w-64">
                  <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                  <Input
                    placeholder={t('searchVehicles')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-11 pl-9"
                  />
                </div>
                <Link
                  to="/dashboard/vehicles/create"
                  className="w-full sm:w-auto"
                >
                  <Button
                    className="primary-bg-gradient w-full whitespace-nowrap sm:w-auto"
                    size="lg"
                  >
                    <Plus className="size-4" />
                    {t('addVehicle')}
                  </Button>
                </Link>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 max-md:px-0">
            {vehicleType === 'trucks' ? (
              <TruckList search={debouncedSearch} />
            ) : (
              <TrailerList search={debouncedSearch} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
